import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  Download,
  PlayArrow,
  RestartAlt,
  SaveAlt,
  StopCircleOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "components/common/ConfirmDialog";
import api from "features/api";
import {
  useDiscardPdfJobMutation,
  useGetPdfJobQuery,
  useLazyGetPdfJobDownloadQuery,
  useRestartPdfJobMutation,
  useStopPdfJobMutation,
  type PdfJobDetail,
} from "features/pdfTools/pdfTools";
import {
  removePdfSplitJob,
  trackPdfSplitJob,
} from "features/pdfSplitJobsSlice";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { startDownloadViaHiddenFrame } from "utils/hiddenFrameDownload";
import { formatApiError } from "utils/formatApiError";
import { formatBytes } from "components/preview/previewTypes";
import JobStatusChip from "./JobStatusChip";
import JobLogsPanel from "./JobLogsPanel";
import JobWorkspacePanel from "./JobWorkspacePanel";
import JobSummaryPanel from "./JobSummaryPanel";
import SaveToStorageDialog from "./SaveToStorageDialog";
import {
  PDF_JOB_PHASE_LABELS,
  PDF_JOB_RESULT_LABELS,
  formatJobDateTime,
} from "./pdfJobUi";

const ACTIVE_STATUSES = ["pending", "running"] as const;

function isActiveStatus(status: string): boolean {
  return (ACTIVE_STATUSES as readonly string[]).includes(status);
}

function publishInProgress(job: PdfJobDetail): boolean {
  return job.publish.status === "pending" || job.publish.status === "running";
}

type DetailTab = "logs" | "workspace" | "summary";

export default function JobDetails({ jobId }: { jobId: string }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [pollMs, setPollMs] = useState(2500);
  const {
    data: job,
    error,
    isLoading,
  } = useGetPdfJobQuery(jobId, {
    pollingInterval: pollMs,
    refetchOnMountOrArgChange: true,
  });

  const [stopJob, { isLoading: stopping }] = useStopPdfJobMutation();
  const [restartJob, { isLoading: restarting }] = useRestartPdfJobMutation();
  const [discardJob, { isLoading: discarding }] = useDiscardPdfJobMutation();
  const [triggerDownload] = useLazyGetPdfJobDownloadQuery();

  const [tab, setTab] = useState<DetailTab>("logs");
  const [saveOpen, setSaveOpen] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [discardDeleteData, setDiscardDeleteData] = useState(true);
  const [discardDeleteSaved, setDiscardDeleteSaved] = useState(false);
  const tabTouchedRef = useRef(false);
  const wasActiveRef = useRef(false);

  const active = !!job && isActiveStatus(job.status);
  const saving = !!job && publishInProgress(job);

  useEffect(() => {
    setPollMs(active || saving ? 2500 : 0);
  }, [active, saving]);

  useEffect(() => {
    if (!job) return;
    if (isActiveStatus(job.status)) {
      wasActiveRef.current = true;
      return;
    }
    if (wasActiveRef.current) {
      wasActiveRef.current = false;
      dispatch(removePdfSplitJob({ jobId }));
      dispatch(
        api.util.invalidateTags([{ type: "PdfSplitJobs", id: "LIST" }]),
      );
    }
  }, [job, jobId, dispatch]);

  useEffect(() => {
    if (!job || tabTouchedRef.current) return;
    if (job.status === "completed") setTab("summary");
  }, [job]);

  const totals = job?.summary?.totals;
  const progressPercent = useMemo(() => {
    if (!job) return null;
    const { total_pages, processed_pages } = job.progress;
    if (!total_pages) return null;
    return Math.min(100, (processed_pages / total_pages) * 100);
  }, [job]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !job) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          Job not found or no longer available.
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/splitting-tools")}>
          Back to jobs
        </Button>
      </Box>
    );
  }

  const handleDownload = async () => {
    try {
      const dl = await triggerDownload(job.job_id).unwrap();
      startDownloadViaHiddenFrame(dl.download_url);
    } catch (err: unknown) {
      dispatch(
        pushNotification({
          message: formatApiError(
            (err as { data?: unknown })?.data,
            "Could not start the download. Try again.",
          ),
          severity: "error",
        }),
      );
    }
  };

  const handleStop = async () => {
    setConfirmStop(false);
    try {
      await stopJob(job.job_id).unwrap();
      dispatch(removePdfSplitJob({ jobId: job.job_id }));
      dispatch(
        pushNotification({
          message: "Job stopped. You can resume it or start over.",
          severity: "info",
        }),
      );
    } catch (err: unknown) {
      dispatch(
        pushNotification({
          message: formatApiError(
            (err as { data?: unknown })?.data,
            "Could not stop the job.",
          ),
          severity: "error",
        }),
      );
    }
  };

  const handleRestart = async (mode: "resume" | "fresh") => {
    setConfirmStartOver(false);
    try {
      const restarted = await restartJob({ jobId: job.job_id, mode }).unwrap();
      dispatch(
        trackPdfSplitJob({
          jobId: restarted.job_id,
          label: `${restarted.user.username} · ${restarted.splitter_label}`,
        }),
      );
      dispatch(
        pushNotification({
          message:
            mode === "resume"
              ? "Resuming the job from where it stopped."
              : "Starting the split over.",
          severity: "info",
        }),
      );
    } catch (err: unknown) {
      dispatch(
        pushNotification({
          message: formatApiError(
            (err as { data?: unknown })?.data,
            "Could not restart the job.",
          ),
          severity: "error",
        }),
      );
    }
  };

  const handleDiscard = async () => {
    setConfirmDiscard(false);
    try {
      await discardJob({
        jobId: job.job_id,
        delete_data: discardDeleteData,
        delete_saved_data: discardDeleteSaved,
      }).unwrap();
      dispatch(
        pushNotification({
          message: discardDeleteData
            ? "The job and its files were deleted."
            : "The job was removed from the list (files kept in storage).",
          severity: "info",
        }),
      );
      navigate("/splitting-tools");
    } catch (err: unknown) {
      dispatch(
        pushNotification({
          message: formatApiError(
            (err as { data?: unknown })?.data,
            "Could not delete the job.",
          ),
          severity: "error",
        }),
      );
    }
  };

  const showSave =
    job.status === "completed" &&
    !job.discarded_at &&
    (job.publish.status === "none" || job.publish.status === "failed");
  const showDiscard =
    !isActiveStatus(job.status) && !job.discarded_at && !saving;
  const showResume =
    ["stopped", "failed"].includes(job.status) && !job.discarded_at && !saving;
  const showStartOver =
    !isActiveStatus(job.status) && !job.discarded_at && !saving;
  const wasSaved = job.publish.status === "completed";
  const phaseLabel =
    PDF_JOB_PHASE_LABELS[job.progress.phase] || job.progress.phase;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minHeight: 0,
        overflow: "auto",
        pr: 0.5,
      }}
    >
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
            {job.user.username || "—"}
          </Typography>
          <Typography color="text.secondary">
            {job.splitter_label}
          </Typography>
          <JobStatusChip status={job.status} />
          {job.result_state && (
            <Chip
              size="small"
              variant="outlined"
              color={
                job.result_state === "saved"
                  ? "success"
                  : job.result_state === "discarded"
                    ? "default"
                    : "info"
              }
              label={PDF_JOB_RESULT_LABELS[job.result_state]}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {active && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<StopCircleOutlined />}
              onClick={() => setConfirmStop(true)}
              disabled={stopping}
            >
              Stop
            </Button>
          )}
          {showResume && (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={() => void handleRestart("resume")}
              disabled={restarting}
            >
              Resume
            </Button>
          )}
          {showStartOver && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAlt />}
              onClick={() => setConfirmStartOver(true)}
              disabled={restarting}
            >
              Start over
            </Button>
          )}
          {showSave && (
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveAlt />}
              onClick={() => setSaveOpen(true)}
            >
              Save to Data Storage
            </Button>
          )}
          {job.has_results_zip && !job.discarded_at && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download />}
              onClick={() => void handleDownload()}
            >
              Download ZIP
            </Button>
          )}
          {showDiscard && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<DeleteOutline />}
              onClick={() => {
                setDiscardDeleteData(true);
                setDiscardDeleteSaved(false);
                setConfirmDiscard(true);
              }}
              disabled={discarding}
            >
              Delete
            </Button>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          Started {formatJobDateTime(job.started_at || job.created_at)} by{" "}
          {job.created_by.username || "—"}
          {job.completed_at
            ? ` · finished ${formatJobDateTime(job.completed_at)}`
            : ""}
          {" · "}
          {job.inputs
            .map(
              (i) =>
                `${i.file_name}${i.size != null ? ` (${formatBytes(i.size)})` : ""}`,
            )
            .join(", ")}
          {typeof job.options.company_name === "string" && job.options.company_name
            ? ` · company: ${job.options.company_name}`
            : ""}
          {job.options.rotate ? " · rotated 90°" : ""}
        </Typography>

        {active && (
          <Box sx={{ mt: 1.5 }}>
            {progressPercent != null ? (
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{ borderRadius: 1, height: 8 }}
              />
            ) : (
              <LinearProgress sx={{ borderRadius: 1, height: 8 }} />
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {phaseLabel || "Starting"}
              {job.progress.total_pages > 0 &&
                ` — ${job.progress.processed_pages.toLocaleString()} of ${job.progress.total_pages.toLocaleString()} pages`}
              {job.progress.current_file && ` · ${job.progress.current_file}`}
              {job.progress.note && ` · ${job.progress.note}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              You can leave this page — processing continues on the server.
            </Typography>
          </Box>
        )}

        {job.status === "failed" && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {job.error_message ||
              "Processing failed. The uploaded files were not changed — you can start a new job."}
          </Alert>
        )}
        {(job.status === "stopped" || job.status === "canceled") && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            This job was stopped. Everything processed so far is kept — resume
            to continue from where it stopped, or start over.
          </Alert>
        )}
        {job.status === "completed" &&
          !job.discarded_at &&
          (totals?.error_pages ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              The job finished, but {totals?.error_pages} page(s) could not be
              matched to employees — review the Error pages section in the
              Workspace tab.
            </Alert>
          )}
        {saving && (
          <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 1.5 }}>
            Saving to Data Storage… Files are being copied to{" "}
            {job.publish.destination}
          </Alert>
        )}
        {job.publish.status === "completed" && (
          <Alert
            severity="success"
            sx={{ mt: 1.5 }}
            action={
              <Button
                size="small"
                color="inherit"
                onClick={() => navigate("/data-storage")}
              >
                View in Data Storage
              </Button>
            }
          >
            Saved to {job.publish.destination}
          </Alert>
        )}
        {job.publish.status === "failed" && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            Saving failed: {job.publish.error || "unknown error"}. You can try
            saving again.
          </Alert>
        )}
        {job.discarded_at && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            The workspace data of this job was discarded on{" "}
            {formatJobDateTime(job.discarded_at)}.
          </Alert>
        )}
      </Paper>

      {!job.discarded_at && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Tabs
            value={tab}
            onChange={(_e, v) => {
              tabTouchedRef.current = true;
              setTab(v);
            }}
            sx={{ mb: 1.5, minHeight: 36, "& .MuiTab-root": { minHeight: 36 } }}
          >
            <Tab value="logs" label="Logs" />
            <Tab value="workspace" label="Workspace" />
            {job.summary && <Tab value="summary" label="Summary" />}
          </Tabs>

          {tab === "logs" && <JobLogsPanel jobId={job.job_id} live={active} />}
          {tab === "workspace" && (
            <JobWorkspacePanel jobId={job.job_id} live={active} />
          )}
          {tab === "summary" && job.summary && <JobSummaryPanel job={job} />}
        </Paper>
      )}

      <SaveToStorageDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        job={job}
      />

      <ConfirmDialog
        open={confirmStop}
        title="Stop this job?"
        message="Processing will pause. Everything produced so far is kept, and you can resume from the same place later."
        confirmLabel="Stop"
        cancelLabel="Keep running"
        onConfirm={() => void handleStop()}
        onCancel={() => setConfirmStop(false)}
        loading={stopping}
      />
      <ConfirmDialog
        open={confirmStartOver}
        title="Start this job over?"
        message="The produced results, error pages and summary will be cleared from the workspace (the uploaded archives are kept) and the split will run again from the beginning."
        confirmLabel="Start over"
        cancelLabel="Keep"
        onConfirm={() => void handleRestart("fresh")}
        onCancel={() => setConfirmStartOver(false)}
        loading={restarting}
      />
      <ConfirmDialog
        open={confirmDiscard}
        title="Delete this job?"
        message={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2">
              The job will disappear from the jobs list.
              {wasSaved
                ? " Files saved to Data Storage are kept unless you tick the second option."
                : " Files already saved to Data Storage are never touched."}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={discardDeleteData}
                  onChange={(e) => setDiscardDeleteData(e.target.checked)}
                />
              }
              label="Also delete the job files from S3 (input, results, error pages)"
            />
            {wasSaved && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={discardDeleteSaved}
                    onChange={(e) => setDiscardDeleteSaved(e.target.checked)}
                  />
                }
                label={`Also delete the files saved to Data Storage (${job.publish.destination})`}
              />
            )}
          </Box>
        }
        confirmLabel="Delete job"
        cancelLabel="Keep"
        onConfirm={() => void handleDiscard()}
        onCancel={() => setConfirmDiscard(false)}
        loading={discarding}
      />
    </Box>
  );
}
